// src/components/desk/FailedEntryModal.jsx — reopening a save that FAILED
// ----------------------------------------------------------------------------
// A commit whose execute_proposal raised leaves an accepted-but-never-executed
// proposal carrying result.error. The feed shows it as a red "not saved" card, and
// clicking it used to only print the error: there was nothing to open, because
// nothing was created (that was itself a 2026-07-16 fix — before it, the click did
// nothing at all and read as a permissions bug).
//
// Owner ruling 2026-08-01: let the agent reopen it, fix it and save.
//
// ⚠️ REOPENING IS A RETRY, NOT A CREATE — and by retry time the world may have moved
// on. The owner's own question: "what if a new one was added with the same phone
// number, and now I open this with a number that is already registered?" A naive
// pre-filled create form would mint a second person for that number (the commit
// falls through to resolve_party, which usually reuses by phone but has a DOCUMENTED
// dedup gap — two identical calls once minted two parties). So this resolves the
// number FIRST and only then decides what to show:
//
//   number already registered → NO create form. Open that client, or retire this
//                               attempt as already-resolved (stamped with the party
//                               it resolved to, so the record says why it left).
//   number not registered     → the pre-filled add form, whose own live pre-check
//                               still catches a person registered seconds ago.
//   saved successfully        → the failed row is retired, superseded by the new
//                               proposal, and leaves the feed.
//
// It can therefore never mint a second person for a number that already exists.
// "Add anyway" stays available because a shared office line is a real thing and the
// pre-check rule has always been NOTIFY, never block (0027: the agent's tap is the
// verdict).

import { useEffect, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { Modal, btn } from './primitives.jsx'
import { findPartyCandidates, supersedeFailedProposal } from '../../db/core.js'
import { ManualClientModal } from './ManualClientModal.jsx'

export function FailedEntryModal({ entry, districts, features, agentId, agentName = null,
                                   onOpenParty, onDone, onClose }) {
  const proposed = entry?.proposed || {}
  const person = proposed.person || {}
  // 'checking' → resolving the number · 'exists' → already registered · 'retry' → the form
  const [stage, setStage] = useState('checking')
  const [cands, setCands] = useState([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    const phone = String(person.phone || '').trim()
    const email = String(person.email || '').trim()
    if (phone.replace(/\D/g, '').length < 6 && !email) { setStage('retry'); return }
    let gone = false
    findPartyCandidates({ phone: phone || null, email: email || null })
      .then(r => {
        if (gone) return
        // never offer our own bridged agents as "this client" (same filter everywhere)
        const list = (r?.candidates || []).filter(c => !c.is_agent)
        setCands(list); setStage(list.length ? 'exists' : 'retry')
      })
      // A pre-check failure must not strand the agent: fall through to the form, which
      // runs the same check again as they type.
      .catch(() => { if (!gone) setStage('retry') })
    return () => { gone = true }
  }, [entry?.id])

  async function retire({ by = null, partyId = null } = {}) {
    if (busy) return
    setBusy(true); setErr('')
    try {
      await supersedeFailedProposal(entry.id, { by, resolvedPartyId: partyId })
      onDone?.(); onClose?.()
    } catch (e) { setErr(e.message); setBusy(false) }
  }

  if (stage === 'retry') {
    return (
      <ManualClientModal districts={districts} features={features}
        // The failed row's own payload IS the draft shape ManualClientModal expects
        // ({person, requirement, criteria}) — the agent sees exactly what didn't save.
        draft={proposed} agentId={agentId} agentName={agentName}
        onClose={onClose}
        onSaved={resp => {
          const ok = (resp?.results || []).find(x => x.command === 'extract_requirement' && x.executed)
          // Retire ONLY on a real save. A second failure leaves the original card alone —
          // hiding a corpse because its replacement also died would lose both.
          if (ok?.proposal_id) retire({ by: ok.proposal_id })
          else onDone?.()
        }} />
    )
  }

  const failReason = (() => {
    const e = entry?.result?.error
    if (!e) return null
    return typeof e === 'string' ? e : (e.message || JSON.stringify(e)).slice(0, 200)
  })()

  return (
    <Modal title={t('desk_failed_entry_title')} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="rounded-lg border border-border px-3 py-2">
          <div className="font-medium">
            <Icon name="user" size={13} /> {person.display_name || person.phone || t('desk_client_fallback_person')}
            {person.phone && person.display_name && <span className="text-muted-foreground text-xs ml-1.5">{person.phone}</span>}
          </div>
          {failReason && (
            <p className="text-xs text-muted-foreground mt-1">{tLog('desk_failed_entry_error', { reason: failReason })}</p>
          )}
        </div>

        {stage === 'checking' && (
          <p className="text-xs text-muted-foreground">{t('desk_failed_entry_checking')}</p>
        )}

        {stage === 'exists' && (
          <>
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-2 space-y-1.5">
              <p className="text-xs font-medium text-amber-600 dark:text-amber-400">{t('desk_failed_entry_exists')}</p>
              {cands.map(c => (
                <div key={c.party_id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">
                    {c.display_name}
                    <span className="text-xs text-muted-foreground ml-1.5">
                      {c.primary_phone || c.primary_email}
                      {c.requirements_count > 0 && <> · {c.requirements_count} <Icon name="search" size={11} /></>}
                      {c.listings_count > 0 && <> · {c.listings_count} <Icon name="house" size={11} /></>}
                    </span>
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <button className="text-xs text-primary" disabled={busy}
                      // Do NOT close this modal (2026-08-04): the person opens ON TOP and
                      // closing it lands back here, where the retry/archive decision this
                      // entry exists for still has to be made. Closing meant looking up the
                      // candidate threw the failed entry away and it had to be found again.
                      onClick={() => onOpenParty?.(c.party_id)}>
                      {t('desk_failed_entry_open')}
                    </button>
                    {/* Retiring records WHICH party resolved it — "superseded", not "vanished". */}
                    <button className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40" disabled={busy}
                      onClick={() => retire({ partyId: c.party_id })}>
                      {t('desk_failed_entry_archive')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {err && <p className="text-xs text-destructive">{err}</p>}
            <div className="flex justify-between items-center">
              {/* Never a dead end: a shared office line means these can be different people. */}
              <button className="text-xs text-muted-foreground underline decoration-dotted"
                onClick={() => setStage('retry')}>{t('desk_failed_entry_add_anyway')}</button>
              <button className={btn('ghost')} onClick={onClose}>{t('meeting_cancel_btn')}</button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
